import type { ApiRequestEntry } from '../types';
import { sanitizeString } from '../sanitize';

type OnRequestCaptured = (entry: Omit<ApiRequestEntry, 'id'>) => void;

interface FetchInterceptorOptions {
  excludePatterns?: string[];
  maxBodyLength?: number;
  onCapture: OnRequestCaptured;
}

const DEFAULT_EXCLUDE = ['/api/auth/session'];
const DEFAULT_MAX_BODY = 2048;

function truncate(str: string | null, max: number): string | null {
  if (!str) return null;
  return str.length > max ? str.substring(0, max) + '...' : str;
}

function extractBody(body: BodyInit | null | undefined, max: number): string | null {
  if (!body) return null;
  try {
    if (typeof body === 'string') return truncate(body, max);
    if (body instanceof FormData) return '[FormData]';
    if (body instanceof ArrayBuffer) return `[ArrayBuffer ${body.byteLength}B]`;
    if (body instanceof Blob) return `[Blob ${body.size}B]`;
    if (body instanceof URLSearchParams) return truncate(body.toString(), max);
    return truncate(JSON.stringify(body), max);
  } catch {
    return null;
  }
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof input !== 'string' && !(input instanceof URL) && input.method) {
    return input.method.toUpperCase();
  }
  return 'GET';
}

const TEXTUAL_CONTENT_TYPES = ['text/', 'application/json', 'application/xml', 'application/xhtml'];

function isReadableResponse(response: Response, maxBodyLength: number): boolean {
  const contentType = response.headers.get('content-type') ?? '';
  const isTextual = TEXTUAL_CONTENT_TYPES.some((t) => contentType.includes(t));
  if (!isTextual) return false;

  const contentLength = response.headers.get('content-length');
  if (contentLength && parseInt(contentLength, 10) > maxBodyLength) return false;

  return true;
}

/**
 * Patches `window.fetch` to capture request/response metadata.
 * Returns an `unregister` function that restores the original fetch.
 */
export function registerFetchInterceptor(options: FetchInterceptorOptions): () => void {
  const {
    excludePatterns = DEFAULT_EXCLUDE,
    maxBodyLength = DEFAULT_MAX_BODY,
    onCapture,
  } = options;

  const originalFetch = window.fetch;

  window.fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = resolveUrl(input);
    const method = resolveMethod(input, init);
    const excluded = excludePatterns.some((p) => url.includes(p));
    const start = performance.now();

    try {
      const response = await originalFetch.call(window, input, init);

      if (!excluded) {
        let responseBody: string | null = null;
        try {
          if (isReadableResponse(response, maxBodyLength)) {
            responseBody = truncate(await response.clone().text(), maxBodyLength);
          }
        } catch { /* ignore */ }

        const rawRequestBody = extractBody(init?.body, maxBodyLength);

        onCapture({
          method,
          url,
          status: response.status,
          statusText: response.statusText,
          duration: Math.round(performance.now() - start),
          timestamp: new Date().toISOString(),
          requestBody: rawRequestBody ? sanitizeString(rawRequestBody) : null,
          responseBody: responseBody ? sanitizeString(responseBody) : null,
          isError: response.status >= 400,
        });
      }

      return response;
    } catch (error) {
      if (!excluded) {
        onCapture({
          method,
          url,
          status: 0,
          statusText: 'Network Error',
          duration: Math.round(performance.now() - start),
          timestamp: new Date().toISOString(),
          requestBody: null,
          responseBody: null,
          isError: true,
        });
      }
      throw error;
    }
  };

  return () => {
    window.fetch = originalFetch;
  };
}
